'use client';

import type { ReactNode } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import type { ToolCallProgressEvent } from '@terra/shared-dto-chat-types';
import { MetaDefault } from '../../../../../components/typography';
import { MarkdownPreview } from '../../../../markdown-preview';
import { InlineToolExpand } from '../../InlineToolExpand';
import { hasRenderableNode } from '../../chat-renderability';
import {
  ToolCallActivityRows,
  toDetailFirstActivityRow,
} from '../ToolCallActivityRows';
import type { CardRenderContext } from '../../tool-config-types';
import {
  readVisibleAgentWriteup,
  readVisibleAnalystNarration,
} from './strip-investigation-writeup';

const HIDDEN_PROGRESS_STAGES = new Set([
  'Investigation started',
  'Investigation completed',
]);

const TALK_STAGES = new Set(['AI']);
const NARRATION_STAGES = new Set(['AI', 'Thinking']);

function readTalkMessages({
  events,
  isRunning,
}: {
  events: ToolCallProgressEvent[];
  isRunning: boolean;
}): string[] {
  return events.reduce<string[]>((messages, event, index) => {
    if (
      !TALK_STAGES.has(event.stage) ||
      !event.detail ||
      event.detail === 'Thinking'
    ) {
      return messages;
    }

    const hasLaterTool = events
      .slice(index + 1)
      .some((laterEvent) => laterEvent.stage === 'Tool');

    if (!isRunning && !hasLaterTool) {
      return messages;
    }

    const message = readVisibleAnalystNarration(event.detail);

    if (!message || messages.at(-1) === message) {
      return messages;
    }

    return [...messages, message];
  }, []);
}

function isVisibleActivityEvent(event: ToolCallProgressEvent): boolean {
  if (
    HIDDEN_PROGRESS_STAGES.has(event.stage) ||
    NARRATION_STAGES.has(event.stage)
  ) {
    return false;
  }

  return Boolean(event.detail || event.stage);
}

function InvestigationTalk({ messages }: { messages: string[] }): ReactNode {
  if (messages.length === 0) {
    return null;
  }

  return (
    <VStack gap={2} alignItems="stretch" width="100%">
      {messages.map((message, index) => (
        <Box
          key={`${index}:${message.slice(0, 40)}`}
          width="100%"
          color={{ _light: 'neutral.light.9', _dark: 'neutral.dark.8' }}
        >
          <MarkdownPreview source={message} compact fontSize={14} />
        </Box>
      ))}
    </VStack>
  );
}

function TraceInvestigationCard({
  ctx,
}: {
  ctx: CardRenderContext;
}): ReactNode {
  const isRunning =
    ctx.isGroupRunning ||
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const progressEvents = ctx.toolCallData.progressEvents ?? [];
  const activityEvents = progressEvents.filter(isVisibleActivityEvent);
  const hasNestedContent = hasRenderableNode(ctx.nestedContent);
  const talkMessages = readTalkMessages({ events: progressEvents, isRunning });
  const writeup = readVisibleAgentWriteup(ctx.toolCallData.output);
  const activity =
    !hasNestedContent && activityEvents.length > 0 ? (
      <ToolCallActivityRows
        rows={activityEvents.map(toDetailFirstActivityRow)}
      />
    ) : null;
  const nestedConversation =
    hasNestedContent || talkMessages.length > 0 || Boolean(writeup) ? (
      <VStack alignItems="stretch" gap={2} width="100%">
        <InvestigationTalk messages={talkMessages} />
        {hasNestedContent ? ctx.nestedContent : null}
        {writeup ? (
          <Box
            width="100%"
            color={{ _light: 'neutral.light.9', _dark: 'neutral.dark.8' }}
          >
            <MarkdownPreview source={writeup} compact fontSize={14} />
          </Box>
        ) : null}
      </VStack>
    ) : undefined;
  const error = ctx.toolCallData.error;
  const expandStatus =
    isRunning && ctx.toolCallData.status !== 'error'
      ? ctx.toolCallData.status === 'started'
        ? 'started'
        : 'in-progress'
      : ctx.toolCallData.status;
  const hasActivity =
    hasRenderableNode(nestedConversation) ||
    Boolean(activity) ||
    Boolean(error);

  if (!hasActivity && !writeup && !isRunning) {
    return null;
  }

  return (
    <VStack alignItems="stretch" gap={2} width="100%">
      {hasActivity || isRunning ? (
        <InlineToolExpand
          toolName={ctx.toolName}
          displayText={isRunning ? ctx.labelPresent : ctx.labelPast}
          status={expandStatus}
          error={error}
          isSubAgent
          forceExpanded={isRunning || ctx.forceExpanded}
          nestedContent={nestedConversation}
          summary={ctx.nestedSummary}
          liveLabel={ctx.runningText}
          expansionKey={ctx.expansionKey}
          headlineKind="investigate-execution"
        >
          {activity}
        </InlineToolExpand>
      ) : null}
      {error && isRunning ? (
        <MetaDefault color="fg.error" whiteSpace="pre-wrap">
          {error}
        </MetaDefault>
      ) : null}
    </VStack>
  );
}

export function renderTraceInvestigationCard(
  ctx: CardRenderContext,
): ReactNode {
  return <TraceInvestigationCard ctx={ctx} />;
}
